import React, { useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { Spin } from 'antd-v5';
import { CloseOutlined } from '@ant-design/icons';
import useGetMergePreview from '../../../../hooks/query/useGetMergePreview';
import { mergeClientsV2 } from '../../../../services/clients';
import { notificationSuccess, notificationError } from '../../../../core/actions';
import MergeSearchStep from './MergeSearchStep';
import MergeReviewStep from './MergeReviewStep';
import './mergeClientModal.scss';

/*
 * Merge-preview API contract:
 *   GET /api/v1/clients/{targetClientId}/merge-preview/{sourceClientId}
 *   - targetClientId = the client to KEEP (current profile)
 *   - sourceClientId = the client to ARCHIVE (searched duplicate)
 *   Response: {
 *     targetClient: { id, name, posRefNum, mobile, email, storeName, assignedAssociateFirstName, ... }
 *     sourceClient: { id, name, posRefNum, mobile, email, storeName, assignedAssociateFirstName, ... }
 *   }
 */

const MergeClientModal = ({ visible, onClose, sourceClient, onMergeComplete }) => {
  const [step, setStep] = useState(0);
  const [selectedClient, setSelectedClient] = useState(null);
  const [isMerging, setIsMerging] = useState(false);

  const sourceClientId = sourceClient?.client_id || sourceClient?.id;

  // First param (clientId) = current profile (kept), second param = searched duplicate (archived)
  const {
    data: previewData,
    isLoading: isPreviewLoading,
    isError: isPreviewError,
  } = useGetMergePreview({
    clientId: sourceClientId,
    sourceClientId: selectedClient?.id,
    enabled: !!selectedClient?.id && !!sourceClientId,
  });

  const handleClientSelected = useCallback(client => {
    setSelectedClient(client);
  }, []);

  // Advance to review step once preview data is loaded
  React.useEffect(() => {
    if (previewData && selectedClient && step === 0) {
      setStep(1);
    }
  }, [previewData, selectedClient, step]);

  // Reset to search if preview fails
  React.useEffect(() => {
    if (isPreviewError && selectedClient) {
      notificationError('Failed to load merge preview');
      setSelectedClient(null);
    }
  }, [isPreviewError, selectedClient]);

  const handleBack = useCallback(() => {
    setStep(0);
    setSelectedClient(null);
  }, []);

  const handleMerge = useCallback(
    async mergePayload => {
      setIsMerging(true);
      setStep(2);
      try {
        await mergeClientsV2({
          clientId: mergePayload.keptClientId,
          sourceClientId: mergePayload.archivedClientId,
          data: mergePayload.data,
          loadingStatusOff: true,
        });
        notificationSuccess('Clients merged successfully');
        onClose();
        if (onMergeComplete) onMergeComplete(mergePayload.archivedClientId);
      } catch (error) {
        // Check for email validation error from backend
        const errorMessage = error?.response?.data?.message;
        const isEmailError =
          Array.isArray(errorMessage) && errorMessage.some(msg => msg.includes('email must be an email'));

        if (isEmailError) {
          notificationError(
            'Invalid email address detected. Please update the client\'s email to a valid format before merging.'
          );
        } else {
          notificationError('Failed to merge clients');
        }

        setIsMerging(false);
        setStep(1);
      }
    },
    [onClose, onMergeComplete],
  );

  const handleOverlayClick = useCallback(
    e => {
      if (e.target === e.currentTarget) {
        onClose();
      }
    },
    [onClose],
  );

  if (!visible) return null;

  // API returns { targetClient (kept), sourceClient (archived) }
  // targetClient = current profile, sourceClient = searched duplicate
  const keptClient = previewData?.targetClient || sourceClient;
  const archiveClient = previewData?.sourceClient || selectedClient;

  // Portal to document.body so the fixed-position overlay isn't clipped by an
  // ancestor that establishes a containing block (Safari does this for any
  // position:fixed descendant of a CSS grid). See ENG-3292.
  return ReactDOM.createPortal(
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div className="merge-modal-overlay" onClick={handleOverlayClick}>
      <div className="merge-modal-card">
        <button className="merge-modal-close" onClick={onClose} type="button">
          <CloseOutlined />
        </button>

        <div className="merge-modal-content">
          {/* Loading state after selecting a client */}
          {selectedClient && isPreviewLoading && step === 0 && (
            <div>
              <h2 className="merge-modal-title">Merge with Another Client</h2>
              <div className="merge-modal-spinner">
                <Spin size="large" />
              </div>
            </div>
          )}

          {/* Step 0: Search */}
          {step === 0 && !selectedClient && (
            <MergeSearchStep sourceClientId={sourceClientId} onSelect={handleClientSelected} />
          )}

          {/* Step 1: Review */}
          {step === 1 && (
            <MergeReviewStep
              keptClient={keptClient}
              sourceClient={archiveClient}
              onBack={handleBack}
              onMerge={handleMerge}
            />
          )}

          {/* Step 2: Merging in progress */}
          {step === 2 && isMerging && (
            <div>
              <h2 className="merge-modal-title">Merging clients...</h2>
              <div className="merge-modal-spinner">
                <Spin size="large" />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default MergeClientModal;
